import FirstPageIcon from '@mui/icons-material/FirstPage'
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight'
import LastPageIcon from '@mui/icons-material/LastPage'
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import Chip from '@mui/material/Chip'
import Link from '@mui/material/Link'
import { useTheme } from '@mui/material/styles'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link as RRLink, useLocation } from 'react-router-dom'
import {
  IGetInvoiceParams,
  IInvoiceDetail,
  IPagination,
} from '~/src/repositories/Invoice/models/Invoice'

interface IColumn {
  id:
    | 'slipNo'
    | 'invoiceNo'
    | 'invoiceType'
    | 'companyName'
    | 'customer'
    | 'dateOfCreation'
    | 'status'
    | 'proofOfPayment'
  label: string
  minWidth?: number
  align?: 'right'
  render: (item: IInvoiceDetail, index: number) => React.ReactNode
}

interface IProformaInvoiceProps {
  invoices: IInvoiceDetail[]
  pagination: IPagination
  onSubmitGetInvoice: (_params?: IGetInvoiceParams) => void
}

interface TablePaginationActionsProps {
  count: number
  page: number
  rowsPerPage: number
  onPageChange: (event: React.MouseEvent<HTMLButtonElement>, newPage: number) => void
}

function TablePaginationActions(props: TablePaginationActionsProps) {
  const theme = useTheme()
  const { count, page, rowsPerPage, onPageChange } = props

  const handleFirstPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, 0)
  }

  const handleBackButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, page - 1)
  }

  const handleNextButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, page + 1)
  }

  const handleLastPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1))
  }

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page">
        {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  )
}

export const ProformaInvoice: React.FC<IProformaInvoiceProps> = ({
  invoices,
  pagination,
  onSubmitGetInvoice,
}): JSX.Element => {
  const { t } = useTranslation()
  const location = useLocation()

  const [page, setPage] = useState(pagination?.currentPage - 1)
  const [rowsPerPage, setRowsPerPage] = useState(pagination?.limitValue)

  const handleChangePage = (event: unknown, newPage: number): void => {
    setPage(newPage)
    onSubmitGetInvoice({ limit: 20, offset: newPage + 1 })
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setRowsPerPage(+event.target.value)
    setPage(0)
  }

  const columns: IColumn[] = [
    {
      id: 'slipNo',
      label: `${t('list_proforma_invoice.slip_no.')}`,
      minWidth: 150,
      render: (item) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            color: 'rgba(0, 0, 0, 0.87)',
          }}
        >
          <Link
            to={`/admin/order/proforma-invoice/detail/${item.id}`}
            state={{
              item: item,
              prevLocationState: { ...location }
            }}
            underline="always"
            component={RRLink}
          >
            {item.invoiceNo}
          </Link>
        </Typography>
      ),
    },
    {
      id: 'invoiceNo',
      label: `${t('list_proforma_invoice.invoice_no.')}`,
      minWidth: 200,
      render: ({ billCode }) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            color: 'rgba(0, 0, 0, 0.87)',
          }}
        >
          {billCode}
        </Typography>
      ),
    },
    {
      id: 'invoiceType',
      label: `${t('list_proforma_invoice.invoice_type')}`,
      minWidth: 200,
      render: ({ invoiceType }) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            color: 'rgba(0, 0, 0, 0.87)',
          }}
        >
          {invoiceType}
        </Typography>
      ),
    },
    {
      id: 'companyName',
      label: `${t('list_proforma_invoice.company_name')}`,
      minWidth: 200,
      render: ({ companyName }) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            color: 'rgba(0, 0, 0, 0.87)',
          }}
        >
          {companyName}
        </Typography>
      ),
    },
    {
      id: 'customer',
      label: `${t('list_proforma_invoice.customer')}`,
      minWidth: 150,
      render: ({ customer }) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            color: 'rgba(0, 0, 0, 0.87)',
          }}
        >
          {customer}
        </Typography>
      ),
    },
    {
      id: 'dateOfCreation',
      label: `${t('list_proforma_invoice.created_date')}`,
      minWidth: 150,
      render: ({ dateOfCreation }) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            color: 'rgba(0, 0, 0, 0.87)',
          }}
        >
          {dateOfCreation}
        </Typography>
      ),
    },
    {
      id: 'status',
      label: `${t('list_proforma_invoice.status')}`,
      minWidth: 120,
      render: ({ status }) => (
        <Chip
          label={status.replace('_', ' ')}
          variant="outlined"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            height: '24px',
            color: 'rgba(0, 0, 0, 0.87)',
          }}
        />
      ),
    },
    {
      id: 'proofOfPayment',
      label: `${t('list_proforma_invoice.proof_of_payment')}`,
      minWidth: 150,
      render: ({ paymentProof }) =>
      paymentProof && (
          <Link
            target={'_blank'}
            href={`${paymentProof.url}`}
            variant="body2"
            sx={{
              fontFamily: 'Roboto',
              fontStyle: 'normal',
              fontSize: '14px',
              lineHeight: '20px',
              letterSpacing: '0.15px',
              color: '#303F9F',
            }}
          >
            {paymentProof.filename}
          </Link>
        ),
    },
  ]

  return (
    <TableContainer component={Paper} sx={{ padding: 1, width: '100%', mb: 2 }}>
      <Table stickyHeader aria-label="sticky table">
        <TableHead>
          <TableRow>
            {columns.map((column, index) => (
              <TableCell
                key={column.id}
                align={column.align}
                style={{ minWidth: column.minWidth, background: '#FFFFFF' }}
              >
                <Box justifyContent={'space-between'} alignItems={'center'} display={'flex'}>
                  <Typography
                    sx={{
                      fontSize: 14,
                      lineHeight: '24px',
                      fontStyle: 'normal',
                      fontWeight: 500,
                    }}
                  >
                    {column.label}
                  </Typography>
                  <Typography
                    sx={{
                      width: '2px',
                      backgroundColor: `${index === columns.length - 1 ? 'white' : '#E0E0E0'}`,
                      height: '14px',
                    }}
                  />
                </Box>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {invoices?.map((item, index) => {
            return (
              <TableRow hover role="checkbox" tabIndex={-1} key={index}>
                {columns.map((column, i) => {
                  return (
                    <TableCell key={i} align={column.align} sx={{ padding: '13.5px' }}>
                      {column.render(item, index)}
                    </TableCell>
                  )
                })}
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
      <TableFooter sx={{ display: 'flex', width: '100%', justifyContent: 'flex-end' }}>
        <TableRow>
          <TablePagination
            rowsPerPageOptions={[]}
            count={pagination?.totalCount}
            sx={{
              fontSize: 12,
              fontStyle: 'normal',
              lineHeight: '19.92px',
              fontWeight: 400,
              borderColor: 'white',
            }}
            rowsPerPage={rowsPerPage}
            page={page}
            SelectProps={{
              inputProps: {
                'aria-label': 'rows per page',
              },
              native: true,
            }}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ActionsComponent={TablePaginationActions}
          />
        </TableRow>
      </TableFooter>
    </TableContainer>
  )
}
